import { useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';

interface UseAudioPlaybackReturn {
  audioElement: HTMLAudioElement | null;
  currentTime: number;
  isPlaying: boolean;
  isReady: boolean;
  createAudioElement: (audioSrc: string) => void;
  playPause: () => void;
  playAgain: () => void;
  seekTo: (time: number) => void;
  cleanup: () => void;
}

export const useAudioPlayback = (): UseAudioPlaybackReturn => {
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
    null
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const createAudioElement = useCallback(
    (audioSrc: string) => {
      // Clean up existing audio element using functional state update
      setAudioElement(prevAudio => {
        if (prevAudio) {
          prevAudio.pause();
        }
        return null;
      });

      const audio = new Audio(audioSrc);
      audio.preload = 'metadata'; // Load metadata early for seeking

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      const handleLoadedMetadata = () => {
        // Metadata loaded: duration is known, seeking is now safe
        setIsReady(true);
      };

      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);

      setAudioElement(audio);
      setCurrentTime(0);
      setIsPlaying(false);
      setIsReady(false);
    },
    [] // No dependencies - uses functional state updates
  );

  const playPause = useCallback(() => {
    setAudioElement(currentAudio => {
      if (!currentAudio) return currentAudio;

      setIsPlaying(currentPlaying => {
        if (currentPlaying) {
          currentAudio.pause();
          return false;
        } else {
          currentAudio.play().catch(error => {
            Sentry.logger.error('Error playing audio', {
              error: String(error),
              context: 'playPause',
            });
          });
          return true;
        }
      });
      return currentAudio;
    });
  }, []); // No dependencies - uses functional state updates

  const playAgain = useCallback(() => {
    setAudioElement(currentAudio => {
      if (!currentAudio) return currentAudio;

      currentAudio.currentTime = 0;
      setCurrentTime(0);
      currentAudio.play().catch(error => {
        Sentry.logger.error('Error playing audio', {
          error: String(error),
          context: 'playAgain',
        });
      });
      setIsPlaying(true);
      return currentAudio;
    });
  }, []); // No dependencies - uses functional state updates

  const seekTo = useCallback(
    (time: number) => {
      setAudioElement(currentAudio => {
        if (!currentAudio) return currentAudio;

        currentAudio.currentTime = time;
        setCurrentTime(time);

        // If audio wasn't playing, start playing from the new position
        setIsPlaying(currentPlaying => {
          if (!currentPlaying) {
            currentAudio.play().catch(error => {
              Sentry.logger.error('Error playing audio', {
                error: String(error),
                context: 'seekTo',
              });
            });
            return true;
          }
          return currentPlaying;
        });
        return currentAudio;
      });
    },
    [] // No dependencies - uses functional state updates
  );

  const cleanup = useCallback(() => {
    setAudioElement(currentAudio => {
      if (currentAudio) {
        currentAudio.pause();
      }
      return null;
    });
    setIsPlaying(false);
    setCurrentTime(0);
    setIsReady(false);
  }, []); // No dependencies - uses functional state updates

  return {
    audioElement,
    currentTime,
    isPlaying,
    isReady,
    createAudioElement,
    playPause,
    playAgain,
    seekTo,
    cleanup,
  };
};
